import { environment } from 'environments/environment'
import { ParentChild } from '../models/corporate-action'
import { CorporateActionSecurity } from '../models/corporate-action-security'
import { CorporateActionSecurityGroup } from '../models/corporate-action-security-group'
import { CorporateActionDetailValueField } from '../models/corporate-actions-details'

/**
 *handleMandatoryKey from name
 */
export function handleMandatoryKey(valueData: string) {
  if (valueData == null) valueData = 'Unknown'
  else if (valueData) valueData = CorporateActionDetailValueField.MANDATORY
  else valueData = 'Voluntary'
  return valueData
}

/**
 * Parse a string of tabular-formatted security groups into a SecurityGroupInput array.
 *
 * Expects any number of newline-separated security groups, where parent and child security
 * subgroups are separated from each other by a tab character, and CUSIPs within each group
 * are separated by commas.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseBulkEntry(data: string): any[] {
  const securityGroupInputs = []
  for (const line of data.split('\n')) {
    const parentsAndChildren = line.split('\t')

    const lineParents = parentsAndChildren[0]
    const lineChildren =
      parentsAndChildren.length == 2 ? parentsAndChildren[1] : null

    if (!lineParents) {
      continue
    }

    securityGroupInputs.push({
      parentCusips: parseCusips(lineParents),
      childCusips: lineChildren ? parseCusips(lineChildren) : [],
    })
  }
  return securityGroupInputs
}

/**
 * Parse a string of comma-separated CUSIPs into an array of CUSIP strings.
 */
export function parseCusips(cusipsInput: string): string[] {
  return cusipsInput
    .split(',')
    .filter(cusip => cusip && cusip.trim().length > 0)
    .map(cusip => cusip.trim())
}

/**
 * Set parent-child cusip for display in detailed grid.
 */
export function setParentChild(
  allSecurities: CorporateActionSecurityGroup[],
): ParentChild[] {
  const parentChild: ParentChild[] = []
  allSecurities.forEach((action: CorporateActionSecurityGroup) => {
    parentChild.push({
      parentVal: action.parentSecurities
        ? setParentValue(action.parentSecurities)
        : '',

      childVal: action.childSecurities
        ? setChildValue(action.childSecurities)
        : '',
    })
  })

  return parentChild
}

/**
 * Set multiple parents in parent-child cusip for display in detailed grid.
 */
function setChildValue(action: CorporateActionSecurity[]): string {
  const val: string[] = []
  action.forEach((child: CorporateActionSecurity) => {
    val.push(child?.cusip + ` (${child?.securityId}) ` + ` ${child?.issuer} `)
  })
  return val.length > 0 ? val.join(' | ') : ''
}

/**
 * Set multiple childs in parent-child cusip for display in detailed grid.
 */
function setParentValue(action: CorporateActionSecurity[]): string {
  const val: string[] = []
  action.forEach((parent: CorporateActionSecurity) => {
    val.push(
      parent?.cusip + ` (${parent?.securityId}) ` + ` ${parent?.issuer} `,
    )
  })
  return val.length > 0 ? val.join(' | ') : ''
}

/**
 * Set displaySecurities for main table grid.
 */
export function displaySecurities(
  key: string,
  allSecurities: CorporateActionSecurityGroup[],
): string {
  if (!allSecurities) {
    return ''
  }

  if (allSecurities.length > 0) {
    if ('parent' == key) {
      if (allSecurities[0].parentSecurities?.length > 0) {
        return allSecurities[0].parentSecurities
          .map(value => value.cusip)
          .join(', ')
      }
    }
    if ('child' == key) {
      if (
        allSecurities[0].childSecurities &&
        allSecurities[0].childSecurities.length > 0
      ) {
        return allSecurities[0].childSecurities
          .map(value => value.cusip)
          .join(', ')
      }
    }
  }
  return ''
}

/**
 * returns status color.
 */
export function statusColor(status: string) {
  switch (status) {
    case 'open':
      return { color: 'hsl(0, 0%, 86%)' } // light grey
    case 'ready for recon':
      return { color: 'hsl(141, 71%, 48%)' } // green
    case 'no action required':
    case 'idca processed':
      return { color: 'hsl(204, 86%, 53%)' } // cyan
    case 'waiting on external parties':
    case 'pending effectiveness':
      return { color: 'hsl(48, 100%, 67%)' } // yellow
  }
  return { color: 'black' }
}

/**
 *handleMandatoryKey from
 */
export function setMandatoryValue(flag: boolean | null) {
  return flag == null ? 'Unknown' : flag ? 'Mandatory' : 'Voluntary'
}

/**
 * check if env is prod
 */
export function isProdEnv() {
  return environment.production
}

export const FEATURE_NOT_ON_PRODUCTION = 'Feature not yet enabled on Production'
